import { teacherProfileAtom } from '@openeducation/oe-it-jotai-atoms';
import { useEnvironmentContext } from '@openeducation/oe-it-providers';
import type { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { QueryKeys } from '../interfaces/query-keys';
import { ActiveClasses } from '../services/active-classes.service';

const TWENTY_SECONDS_DELAY_MS = 20000;

export function useActiveClassesStatus() {
  const queryClient = useQueryClient();
  const { environment } = useEnvironmentContext();
  const teacherProfile = useAtomValue(teacherProfileAtom);

  const {
    isLoading: isLoadingActiveClassesStatus,
    refetch,
    data: activeClassesStatus,
  } = useQuery<boolean, AxiosError>(
    [QueryKeys.StatusActiveClass],
    () => ActiveClasses.getActiveClassStatus(environment),
    {
      refetchInterval: TWENTY_SECONDS_DELAY_MS,
      refetchOnWindowFocus: true,
      retry: false,
    }
  );

  const {
    mutateAsync: enableLiveClasses,
    isLoading: isLoadingLiveClassEnable,
  } = useMutation(
    () => {
      return ActiveClasses.enableLiveClasses(environment, teacherProfile?.id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKeys.StatusActiveClass);
      },
    }
  );

  const {
    mutateAsync: disableLiveClasses,
    isLoading: isLoadingLiveClassDisable,
  } = useMutation(
    () => {
      return ActiveClasses.disableLiveClasses(environment, teacherProfile?.id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKeys.StatusActiveClass);
      },
    }
  );

  return {
    enableLiveClasses,
    disableLiveClasses,
    activeClassesStatus,
    isLoadingActiveClassesStatus,
    isLoadingChangeActiveClassesStatus:
      isLoadingLiveClassDisable || isLoadingLiveClassEnable,
    refetchActiveClassesStatus: refetch,
  };
}
